import {Title, Text, Divider, Group, Flex, Stack, Loader, Tooltip, ActionIcon} from "@mantine/core";
import {ReactNode, SVGProps} from "react";
import {FaExternalLinkAlt} from "react-icons/fa";
import {ExternalLink} from "@components/ExternalLink.tsx";
import {IconBase} from "react-icons";

export const SettingsSectionHeader = ({
                                          title,
                                          description,
                                          note,
                                          link,
                                          action,
                                      }: {
    title: string;
    description: string;
    note?: ReactNode;
    link?: string;
    action?: ReactNode;
}) => (
    <div>
        <Group align={"flex-end"} gap={"xl"} justify="space-between">
            <Stack>
                <Group align={"flex-end"}>
                    <Title order={1} mt="md">{title}</Title>
                    {note && (
                        <ExternalLink href={link ?? ""}>
                            <Tooltip
                                label={note}
                                position="right"
                                withArrow
                                color={"blue.9"}
                                multiline={true}
                                maw={520}
                            >
                                <ActionIcon size="sm" color={"blue.9"}>
                                    <FaExternalLinkAlt/>
                                </ActionIcon>
                            </Tooltip>
                        </ExternalLink>
                    )}
                </Group>
                <Text>{description}</Text>
            </Stack>
            {action}
        </Group>
        <Divider mt={"md"} size={"md"}/>
    </div>
);

export const StatusIndicator = ({
                                    label,
                                    status,
                                    loadStatus,
                                }: {
    label: string;
    status: boolean | null;
    loadStatus: boolean | null;
}) => (
    <Flex align="center" justify="flex-start">
        <Text size="xl" fw={600}>{label}</Text>
        {loadStatus ? (
            <Loader
                size={"sm"}
                ml={"xs"}
            />
        ) : (
            <Text
                c={status === null ? "gray" : status ? "green" : "red"}
                size="md"
                fw={600}
                ml="xs"
                mt={3}
            >
                {status === null ? "Unknown" : status ? "OK" : "Failed"}
            </Text>
        )}
    </Flex>
);

export const CenteredEmptyState = ({
                                       message,
                                       button,
                                   }: {
    message: string;
    button?: ReactNode;
}) => (
    <Stack mt={"md"}>
        <Group justify="center">
            <Text size="md" fw={600}>{message}</Text>
        </Group>
        <Group justify="center">{button}</Group>
    </Stack>
);

const commonSVGProps: SVGProps<SVGSVGElement> = {
    clipRule: "evenodd",
    fill: "currentColor",
    fillRule: "evenodd",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
    role: "img",
};

export const TVDBIcon = () => (
    <IconBase size={40}>
        <svg{...commonSVGProps} viewBox="0 0 13 13">
            <path
                d="m 1.7844605,10.190309 c -0.18713,-0.1871 -0.16477,-0.051 -0.51038,-3.1043001 -0.31619004,-2.7933 -0.32906004,-3.0127 -0.18855,-3.2133 0.20306,-0.2899 0.35119,-0.2847 3.38314,0.1182 1.5657,0.2081 2.90794,0.4091 2.98277,0.4467 0.20486,0.1029 0.27728,0.3277 0.27728,0.8604 0,0.4646 -0.008,0.4914 -0.22973,0.7364 -0.66325,0.7342 -0.66447,1.7632 -0.003,2.4001 0.20736,0.1996 0.23128,0.2567 0.23188,0.5529 5.1e-4,0.2526 -0.0334,0.3699 -0.14403,0.4985 -0.14015,0.1629 -0.22084,0.179 -2.55946,0.5106 -2.7992,0.3968001 -3.02365,0.4103001 -3.24009,0.1938001 z m 2.1925,-1.8830001 c 0,-0.2521 -0.002,-0.2543 -0.24164,-0.2543 -0.1329,0 -0.27597,-0.034 -0.31794,-0.076 -0.05,-0.05 -0.0763,-0.3143 -0.0763,-0.7658 l 0,-0.6894 0.48353,0.019 0.48354,0.019 0.39397,0.9857 0.39397,0.9856 0.37838,0.017 0.37838,0.017 0.49075,-1.1616 c 0.26992,-0.6389 0.50968,-1.2133 0.5328,-1.2765 0.0377,-0.1031 0.007,-0.1129 -0.29745,-0.095 l -0.33948,0.02 -0.34779,0.8744 c -0.19128,0.4809 -0.36856,0.8743 -0.39396,0.8743 -0.0254,0 -0.13202,-0.236 -0.23694,-0.5246 -0.10493,-0.2885 -0.25277,-0.6891 -0.32853,-0.8902 l -0.13776,-0.3657 -0.7267,0 -0.72671,0 0,-0.5087 0,-0.5087 -0.34974,0 -0.34974,0 0,0.5087 0,0.5087 -0.22256,0 c -0.21726,0 -0.22256,0.01 -0.22256,0.2544 0,0.2465 0.007,0.2544 0.21472,0.2544 l 0.21472,0 0.0299,0.7471 c 0.0438,1.096 0.1763,1.2804 0.92375,1.2852 l 0.39743,0 0,-0.2544 z m 2.98407,-2.6475 c 0.17076,-0.2171 0.16614,-0.3126 -0.0243,-0.503 -0.19684,-0.1968 -0.31765,-0.1968 -0.51449,0 -0.19564,0.1956 -0.20111,0.4329 -0.013,0.5641 0.21276,0.1484 0.40363,0.1273 0.55179,-0.061 z m 0.88409,2.773 c -0.42812,-0.2261 -0.65774,-0.7228 -0.61236,-1.3245 0.0359,-0.4765 0.20564,-0.8102 0.51695,-1.0163 0.22173,-0.1469 0.33378,-0.1722 0.84851,-0.1913 l 0.59305,-0.022 0,-0.4708 0,-0.4708 0.38153,0 0.38154,0 0,1.8123 0,1.8123 -0.93794,0 c -0.80185,0 -0.9718,-0.021 -1.17128,-0.1261 z m 1.34615,-1.1747 0,-0.7312 -0.38643,0 c -0.34232,0 -0.40764,0.021 -0.5723,0.1858 -0.25433,0.2544 -0.26586,0.7268 -0.0247,1.0133 0.17036,0.2025 0.27757,0.2408 0.7132,0.2547 l 0.27026,0.01 0,-0.7313 z m 1.0810095,-0.5087 0,-1.8123 0.38154,0 0.38153,0 0,0.4769 0,0.477 0.51076,0 c 1.05257,0 1.49508,0.4262 1.45089,1.3975 -0.0183,0.4032 -0.0525,0.5334 -0.19712,0.752 -0.29504,0.4458 -0.5269,0.5212 -1.60302,0.5212 l -0.92458,0 0,-1.8123 z m 1.47191,1.1749 c 0.38358,-0.1066 0.57492,-0.5705 0.41833,-1.0143 -0.0988,-0.2798 -0.30034,-0.381 -0.76153,-0.3822 l -0.36564,-9e-4 0,0.7312 0,0.7313 0.23846,-5e-4 c 0.13115,0 0.34282,-0.029 0.47038,-0.065 z"/>
        </svg>
    </IconBase>
);

export const TMDBIcon = () => (
    <IconBase size={40}>
        <svg {...commonSVGProps} viewBox="0 0 24 24">
            <path
                d="M6.62 12a2.291 2.291 0 0 1 2.292-2.295h-.013A2.291 2.291 0 0 1 11.189 12a2.291 2.291 0 0 1-2.29 2.291h.013A2.291 2.291 0 0 1 6.62 12zm10.72-4.062h4.266a2.291 2.291 0 0 0 2.29-2.291 2.291 2.291 0 0 0-2.29-2.296H17.34a2.291 2.291 0 0 0-2.291 2.296 2.291 2.291 0 0 0 2.29 2.29zM2.688 20.645h8.285a2.291 2.291 0 0 0 2.291-2.292 2.291 2.291 0 0 0-2.29-2.295H2.687a2.291 2.291 0 0 0-2.291 2.295 2.291 2.291 0 0 0 2.29 2.292zm10.881-6.354h.81l1.894-4.586H15.19l-1.154 3.008h-.013l-1.135-3.008h-1.154zm4.208 0h1.011V9.705h-1.011zm2.878 0h3.235v-.93h-2.223v-.933h1.99v-.934h-1.99v-.855h2.107v-.934h-3.112zM1.31 7.941h1.01V4.247h1.31v-.895H0v.895h1.31zm3.747 0h1.011V5.959h1.958v1.984h1.011v-4.59h-1.01v1.711H6.061V3.351H5.057zm5.348 0h3.242v-.933H11.41v-.934h1.99v-.933h-1.99v-.856h2.107v-.934h-3.112zM.162 14.296h1.005v-3.52h.013l1.167 3.52h.765l1.206-3.52h.013v3.52h1.011v-4.59H3.82L2.755 12.7h-.013L1.686 9.705H.156zm14.534 6.353h1.641a3.188 3.188 0 0 0 .98-.149 2.531 2.531 0 0 0 .824-.437 2.123 2.123 0 0 0 .567-.713 2.193 2.193 0 0 0 .223-.983 2.399 2.399 0 0 0-.218-1.07 1.958 1.958 0 0 0-.586-.716 2.405 2.405 0 0 0-.873-.392 4.349 4.349 0 0 0-1.046-.13h-1.519zm1.013-3.656h.596a2.26 2.26 0 0 1 .606.08 1.514 1.514 0 0 1 .503.244 1.167 1.167 0 0 1 .34.412 1.28 1.28 0 0 1 .13.587 1.546 1.546 0 0 1-.13.658 1.127 1.127 0 0 1-.347.433 1.41 1.41 0 0 1-.518.238 2.797 2.797 0 0 1-.649.07h-.538zm4.686 3.656h1.88a2.997 2.997 0 0 0 .613-.064 1.735 1.735 0 0 0 .554-.214 1.221 1.221 0 0 0 .402-.39 1.105 1.105 0 0 0 .155-.606 1.188 1.188 0 0 0-.071-.415 1.01 1.01 0 0 0-.204-.34 1.087 1.087 0 0 0-.317-.24 1.297 1.297 0 0 0-.413-.13v-.012a1.203 1.203 0 0 0 .575-.366.962.962 0 0 0 .216-.648 1.081 1.081 0 0 0-.149-.603 1.022 1.022 0 0 0-.389-.354 1.673 1.673 0 0 0-.54-.169 4.463 4.463 0 0 0-.6-.041h-1.712zm1.011-3.734h.687a1.4 1.4 0 0 1 .24.022.748.748 0 0 1 .22.075.432.432 0 0 1 .16.147.418.418 0 0 1 .061.236.47.47 0 0 1-.055.233.433.433 0 0 1-.146.156.62.62 0 0 1-.204.084 1.058 1.058 0 0 1-.23.026h-.745zm0 1.835h.765a1.96 1.96 0 0 1 .266.02 1.015 1.015 0 0 1 .26.07.519.519 0 0 1 .204.152.406.406 0 0 1 .08.26.481.481 0 0 1-.06.253.519.519 0 0 1-.16.168.62.62 0 0 1-.217.09 1.155 1.155 0 0 1-.237.027H21.4z"/>
        </svg>
    </IconBase>
);

export const GotifyIcon = () => (
    <IconBase>
        <svg {...commonSVGProps} viewBox="0 0 140 140">
            <path
                d="m 114.5,21.4 c -11.7,0 -47.3,5.9 -54.3,7.1 -47.3,8.0 -48.4,9.9 -50.1,12.8 -1.2,2.1 -2.4,4.0 2.6,29.4 2.3,11.5 5.8,26.9 8.8,35.8 1.8,5.4 3.6,8.8 6.9,10.1 0.8,0.3 1.7,0.5 2.7,0.6 0.2,0.0 0.3,0.0 0.5,0.0 12.8,0 89.1,-19.5 89.9,-19.7 1.4,-0.4 4.0,-1.5 5.3,-5.1 1.8,-4.7 1.9,-16.7 0.5,-35.7 -2.1,-28.0 -4.1,-31.0 -4.8,-32.0 -2.0,-3.1 -5.6,-3.3 -6.7,-3.3 -0.4,-0.0 -0.9,-0.0 -1.4,-0.0 z m -1.9,6.6 c -9.3,12.0 -18.9,24.0 -25.9,32.4 -2.3,2.8 -4.3,5.1 -6.0,7.0 -1.7,1.9 -2.9,3.2 -3.8,4.0 l -0.3,0.3 -0.4,-0.1 c -1.0,-0.3 -2.5,-0.9 -4.4,-1.7 -2.3,-1.0 -5.2,-2.3 -8.8,-3.9 C 51.6,60.7 34.4,52.2 18.0,43.6 30.3,39.7 95.0,28.7 112.6,27.9 Z m 5.7,5.0 c 2.0,11.8 4.5,42.6 3.1,54.0 -1.8,-1.4 -10.1,-8.0 -19.8,-15.2 -3.0,-2.3 -5.9,-4.3 -8.4,-6.1 l -0.7,-0.5 0.5,-0.6 C 99.5,56.9 108.0,46.2 118.3,32.9 Z M 16.1,51.1 c 3.0,1.5 14.3,7.4 27.4,13.8 5.3,2.6 9.9,4.8 13.9,6.7 l 0.9,0.4 -0.7,0.8 C 50.3,81.2 40.6,92.8 28.8,107.2 24.5,96.7 17.9,65.0 16.1,51.1 Z m 71.5,19.7 0.6,0.4 c 7.8,5.5 18.1,13.2 27.9,21.0 C 104.9,95.1 53.2,107.9 36.0,110.3 46.6,97.4 57.3,84.7 65.1,75.8 l 0.4,-0.4 0.5,0.2 c 5.7,2.5 9.3,3.7 11.1,3.8 0.1,0.0 0.2,0.0 0.3,0.0 0.6,0 1.0,-0.1 1.4,-0.3 0.6,-0.2 2.0,-0.7 8.3,-7.7 z"/>
        </svg>
    </IconBase>
);


